import Balloon from './Balloon'
import Icon from './Icon'
import { Brief, Progress, Launch } from './Drawings'
import cv from '../constants/nikita-yeldynov-resume.pdf'

const STACK = [
  [
    'Front end',
    [
      'Next.js',
      'React',
      'TypeScript',
      'Tailwind CSS',
      'shadcn/ui',
      'Framer Motion',
      'TanStack Query',
      'Zustand',
    ],
  ],
  ['Mobile', ['React Native', 'Expo']],
  ['Back end', ['Node.js', 'NestJS', 'Express', 'Server Actions']],
  ['Data', ['PostgreSQL', 'Prisma', 'Drizzle', 'Supabase', 'Firebase', 'Sanity']],
  ['Auth and payments', ['Clerk', 'Better Auth', 'Stripe']],
  ['Testing and deploy', ['Jest', 'Playwright', 'Vercel', 'GitHub Actions', 'Sentry']],
]

// The three jobs clients bring, told as one tier of panels.
const OFFERS = [
  {
    caption: 'Need a front end built?',
    Drawing: Brief,
    points: [
      'Pixel-accurate Figma to React or Next.js, responsive on every screen',
      'Design systems with Tailwind CSS, shadcn/ui and Storybook',
      'Accessibility, SEO and Core Web Vitals from day one',
    ],
  },
  {
    caption: 'Need the whole app?',
    Drawing: Launch,
    points: [
      'Sign-up, user roles, Stripe payments and admin panels',
      'APIs and Server Actions on PostgreSQL, Supabase or Firebase',
      'AI features like semantic search and chat',
      'React Native and Expo when you need iOS and Android too',
    ],
  },
  {
    caption: 'Need something fixed or faster?',
    Drawing: Progress,
    points: [
      'Messy UI, layout bugs and breaking state, in AI-generated or inherited code',
      'Slow pages: I’ve cut load times by 30% before',
      'An honest audit first: rescue it or rebuild it',
    ],
  },
]

const About = () => (
  <section
    id='about'
    tabIndex={-1}
    aria-labelledby='about-title'
    className='mx-auto max-w-page scroll-mt-20 px-gutter pt-section'
  >
    <h2 id='about-title' className='mb-4 font-display uppercase text-h2 md:mb-5'>
      How I work
    </h2>

    <div className='grid gap-tier md:grid-cols-12'>
      <div className='panel bg-sand p-5 pt-14 md:col-span-7 md:p-8 md:pt-16'>
        <p className='caption absolute left-0 top-0'>Nikita</p>
        <Balloon className='w-fit max-w-[22ch] text-balloon'>
          <p className='font-letter'>Mostly freelance, mostly Next.js.</p>
        </Balloon>
        <div className='mt-12 max-w-[60ch] space-y-4 text-lead text-coal dark:text-ink'>
          <p>
            I&apos;m Nikita. I turn designs into fast, accessible web apps with React, Next.js and TypeScript.
            When a project needs a backend, I build that too, with Node.js and PostgreSQL.
          </p>
          <p>
            I learned to code before AI tools existed, so I catch their mistakes. AI handles the routine
            parts, which makes delivery faster and cheaper for you. I review every line before it ships.
          </p>
        </div>
      </div>

      <div className='panel flex flex-col gap-6 p-5 pt-14 md:col-span-5 md:p-8 md:pt-16'>
        <p className='caption absolute left-0 top-0'>The toolbox</p>
        <dl className='space-y-3'>
          {STACK.map(([group, tools]) => (
            <div key={group}>
              <dt className='lettering text-ink/75'>{group}</dt>
              <dd className='mt-1 flex flex-wrap gap-1.5'>
                {tools.map((t) => (
                  <span key={t} className='lettering border-stroke border-line px-2 py-0.5'>
                    {t}
                  </span>
                ))}
              </dd>
            </div>
          ))}
        </dl>
        <a href={cv} download='nikita-yeldynov-resume' className='btn self-start text-[1.25rem]'>
          Download CV <span className='text-coal/70'>(PDF)</span> <Icon name='down' />
        </a>
      </div>

      {OFFERS.map(({ caption, Drawing, points }) => (
        <div key={caption} className='panel flex flex-col md:col-span-4'>
          <h3 className='caption self-start'>{caption}</h3>
          <div className='mx-auto h-36 w-full max-w-[15rem] px-4 pt-2'>
            <Drawing />
          </div>
          <ul className='space-y-2 px-5 pb-5 pt-3 text-body'>
            {points.map((p) => (
              <li key={p} className='flex gap-2'>
                <span aria-hidden='true' className='mt-2.5 size-2 shrink-0 bg-line' />
                {p}
              </li>
            ))}
          </ul>
        </div>
      ))}
    </div>
  </section>
)

export default About
